import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { useConfigStore } from '@/stores';
import { useOrientation } from '@/hooks/useOrientation';
import useBaseSize from '@/hooks/useBaseSize';
import { getHSize, getSize } from '@/utils/size';
import type {
    UseVirtualizedCarouselParams,
    UseVirtualizedCarouselReturn,
    VirtualSlideData,
    CarouselState,
    CarouselComputedValues,
    CarouselStyleCalculations,
    CarouselNavigationHandlers,
} from '@/types/carousel';

/**
 * Custom hook for virtualized carousel logic
 * Encapsulates common carousel functionality to reduce boilerplate code
 *
 * @param contents - Array of carousel items
 * @param config - Carousel configuration object
 * @param dependencies - Additional dependencies for useEffect (optional)
 * @returns Object containing all carousel state, handlers, and computed values
 */
export const useVirtualizedCarousel = ({ contents, config, dependencies = [] }: UseVirtualizedCarouselParams): UseVirtualizedCarouselReturn => {
    // Store and context hooks
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const { isHorizontal } = useOrientation();
    const { baseWidth, baseHeight } = useBaseSize();

    // Refs
    const swiperRef = useRef<SwiperType | null>(null);
    const prevButtonRef = useRef<HTMLButtonElement | null>(null);
    const nextButtonRef = useRef<HTMLButtonElement | null>(null);

    // State
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

    // ===== COMPUTED VALUES =====
    const isHalfScreenMode = screenMode === 'split';

    const rowsPerSlide = isHorizontal
        ? isHalfScreenMode
            ? config.ROWS_PER_SLIDE.HORIZONTAL.HALF_SCREEN
            : config.ROWS_PER_SLIDE.HORIZONTAL.FULL_SCREEN
        : isHalfScreenMode
          ? config.ROWS_PER_SLIDE.VERTICAL.HALF_SCREEN
          : config.ROWS_PER_SLIDE.VERTICAL.FULL_SCREEN;

    const itemsPerRow = isHorizontal ? config.ITEMS_PER_ROW.HORIZONTAL : config.ITEMS_PER_ROW.VERTICAL;

    const itemsPerSlide = itemsPerRow * rowsPerSlide;
    const totalSlides = Math.ceil(contents.length / itemsPerSlide);

    const cardHeight = !isHorizontal
        ? isHalfScreenMode
            ? config.SLIDE_ITEM_HEIGHT.VERTICAL_HALF_SCREEN
            : config.SLIDE_ITEM_HEIGHT.VERTICAL
        : isHalfScreenMode
          ? config.SLIDE_ITEM_HEIGHT.HORIZONTAL_HALF_SCREEN
          : config.SLIDE_ITEM_HEIGHT.HORIZONTAL;

    const currentPage = useMemo(() => currentSlideIndex + 1, [currentSlideIndex]);

    // ===== COMPUTED NAVIGATION STATE =====
    // Calculate isBeginning and isEnd based on current state, not just swiper events
    const isBeginning = useMemo(() => {
        return currentSlideIndex === 0;
    }, [currentSlideIndex]);

    const isEnd = useMemo(() => {
        return totalSlides <= 1 || currentSlideIndex >= totalSlides - 1;
    }, [currentSlideIndex, totalSlides]);

    // ===== STYLE CALCULATIONS =====
    const containerWidth =
        itemsPerRow *
            (isHorizontal
                ? isHalfScreenMode
                    ? config.SLIDE_ITEM_WIDTH.HORIZONTAL_HALF_SCREEN
                    : config.SLIDE_ITEM_WIDTH.HORIZONTAL
                : isHalfScreenMode
                  ? config.SLIDE_ITEM_WIDTH.VERTICAL_HALF_SCREEN
                  : config.SLIDE_ITEM_WIDTH.VERTICAL) +
        (itemsPerRow - 1) * (isHorizontal ? config.SLIDE_COLUMN_GAP : config.SLIDE_COLUMN_GAP_VERTICAL) +
        config.CONTAINER_PADDING;

    const swiperWidth =
        itemsPerRow *
            (isHorizontal
                ? isHalfScreenMode
                    ? config.SLIDE_ITEM_WIDTH.HORIZONTAL_HALF_SCREEN
                    : config.SLIDE_ITEM_WIDTH.HORIZONTAL
                : isHalfScreenMode
                  ? config.SLIDE_ITEM_WIDTH.VERTICAL_HALF_SCREEN
                  : config.SLIDE_ITEM_WIDTH.VERTICAL) +
        (itemsPerRow - 1) * (isHorizontal ? config.SLIDE_COLUMN_GAP : config.SLIDE_COLUMN_GAP_VERTICAL);

    const navigationButtonStyle = {
        left: `-${getSize(config.NAVIGATION_BUTTON_OFFSET, baseWidth)}`,
        right: `-${getSize(config.NAVIGATION_BUTTON_OFFSET, baseWidth)}`,
    };

    const getGridStyle = useCallback(
        (actualRows: number) => ({
            gridTemplateRows: `repeat(${rowsPerSlide}, ${getHSize(cardHeight, baseHeight)})`,
            gridTemplateColumns: `repeat(${itemsPerRow}, ${getSize(
                isHorizontal
                    ? isHalfScreenMode
                        ? config.SLIDE_ITEM_WIDTH.HORIZONTAL_HALF_SCREEN
                        : config.SLIDE_ITEM_WIDTH.HORIZONTAL
                    : isHalfScreenMode
                      ? config.SLIDE_ITEM_WIDTH.VERTICAL_HALF_SCREEN
                      : config.SLIDE_ITEM_WIDTH.VERTICAL,
                baseWidth
            )})`,
            gap: `${getHSize(isHorizontal ? config.SLIDE_ROW_GAP : config.SLIDE_ROW_GAP_VERTICAL, baseHeight)} ${getSize(isHorizontal ? config.SLIDE_COLUMN_GAP : config.SLIDE_COLUMN_GAP_VERTICAL, baseWidth)}`,
            minHeight: `${getHSize(cardHeight * rowsPerSlide + (isHorizontal ? config.SLIDE_ROW_GAP : config.SLIDE_ROW_GAP_VERTICAL) * (rowsPerSlide - 1), baseHeight)}`,
            // actualRows is available for custom grid calculations if needed
            ...(actualRows && {}),
        }),
        [cardHeight, itemsPerRow, baseHeight, baseWidth, rowsPerSlide, config, isHorizontal]
    );

    // ===== VIRTUALIZED SLIDES DATA =====
    const virtualSlidesData: VirtualSlideData[] = useMemo(() => {
        return Array.from({ length: totalSlides }, (_, slideIndex) => {
            const startIndex = slideIndex * itemsPerSlide;
            const slideContents = contents.slice(startIndex, startIndex + itemsPerSlide);
            const actualRows = Math.ceil(slideContents.length / itemsPerRow);

            return {
                slideIndex,
                contents: slideContents,
                actualRows,
                // Virtual properties
                isVisible: Math.abs(slideIndex - currentSlideIndex) <= config.PRELOAD_SLIDES,
                shouldRender: Math.abs(slideIndex - currentSlideIndex) <= config.VIRTUAL_CACHE_SIZE,
                isInView: slideIndex === currentSlideIndex,
            };
        });
    }, [totalSlides, itemsPerSlide, contents, itemsPerRow, currentSlideIndex, config]);

    // ===== EVENT HANDLERS =====
    const handleSlideChange = useCallback((swiper: SwiperType) => {
        setCurrentSlideIndex(swiper.activeIndex);
    }, []);

    const handlePrevClick = useCallback(() => {
        if (swiperRef.current && !isBeginning) {
            swiperRef.current.slidePrev();
        }
    }, [isBeginning]);

    const handleNextClick = useCallback(() => {
        if (swiperRef.current && !isEnd) {
            swiperRef.current.slideNext();
        }
    }, [isEnd]);

    // ===== VIRTUAL SLIDE RENDERER HELPER =====
    const getVirtualSlideProps = useCallback(
        (slideData: VirtualSlideData) => {
            if (!slideData.shouldRender) {
                return null;
            }

            const gridStyle = getGridStyle(slideData.actualRows);
            const slideContents = slideData.contents.map((content, itemIndex) => {
                const isCurrentSlide = slideData.slideIndex === currentSlideIndex;
                const isVisibleInCurrentSlide = isCurrentSlide && itemIndex < itemsPerSlide;
                const tabIndex = isVisibleInCurrentSlide ? 2 : -1;

                return {
                    content,
                    itemIndex,
                    tabIndex,
                    key: content.id,
                    isRegionNav: isCurrentSlide && itemIndex === 0,
                };
            });

            return {
                slideIndex: slideData.slideIndex,
                gridStyle,
                slideContents,
                className: 'grid justify-items-center',
                isInView: slideData.isInView,
            };
        },
        [currentSlideIndex, itemsPerSlide, getGridStyle]
    );

    // ===== EFFECTS =====
    useEffect(() => {
        // Reset to first slide when contents or screen mode changes
        setCurrentSlideIndex(0);
        if (swiperRef.current) {
            swiperRef.current.slideTo(0, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contents, screenMode, ...dependencies]);

    // Additional effect to handle cases where swiper isn't ready yet
    useEffect(() => {
        if (swiperRef.current && currentSlideIndex === 0) {
            // Ensure swiper is synced with our state
            if (swiperRef.current.activeIndex !== 0) {
                swiperRef.current.slideTo(0, 0);
            }
        }
    }, [currentSlideIndex, totalSlides]);

    // ===== RETURN VALUES =====
    const state: CarouselState = {
        isBeginning,
        isEnd,
        currentSlideIndex,
        currentPage,
        totalSlides,
    };

    const computed: CarouselComputedValues = {
        isHalfScreenMode,
        rowsPerSlide,
        itemsPerRow,
        itemsPerSlide,
        cardHeight,
        containerWidth,
        swiperWidth,
    };

    const styles: CarouselStyleCalculations = {
        navigationButtonStyle,
        getGridStyle,
    };

    const navigation: CarouselNavigationHandlers = {
        handlePrevClick,
        handleNextClick,
        handleSlideChange,
    };

    return {
        swiperRef,
        prevButtonRef,
        nextButtonRef,
        state,
        computed,
        styles,
        virtualSlidesData,
        navigation,
        getVirtualSlideProps,
    };
};
